import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ArrowLeft, ArrowRight, Copy, Globe, KeyRound, ShieldCheck, Trash2, UserPlus, Users } from 'lucide-react';
import { ApiError } from '../api/client';
import * as authApi from '../api/auth';
import { useAuth } from '../auth/AuthContext';
import { ProgramSwitcher } from '../components/ProgramSwitcher';
import { AccountMember, AccountRole, ROLE_DESCRIPTIONS, ROLE_LABELS } from '../types/auth';
import { getTeams, SavedTeam } from '../utils/storage';
import { fetchTeamCapacity, TeamCapacity } from '../api/teamCapacity';

/** Tiers the primary holder can hand out. Ownership is not transferable here. */
const ASSIGNABLE_ROLES: AccountRole[] = ['contributor', 'coach', 'viewer', 'sponsor', 'athlete'];

/** The one tier a contributor may hand out themselves — see AccountAccessService#requireMemberManage. */
const CONTRIBUTOR_ASSIGNABLE_ROLES: AccountRole[] = ['athlete'];

/**
 * Everything about running the program itself — starting it, who else can reach it,
 * and its teams — kept apart from {@code Profile}, which is only the signed-in
 * person's own login details.
 */
export function ProgramManagement() {
  const { activeAccount, activeAccountId, canAdminister, canWrite } = useAuth();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Program Management</h1>
            <p className="text-blue-300">Start your program, decide who else can reach it, and manage its teams.</p>
          </div>
          <Link
            to="/options"
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </Link>
        </div>

        <ProgramSwitcher />

        {canWrite && activeAccountId && <TeamsSection accountId={activeAccountId} />}

        {(canAdminister || canWrite) && activeAccountId && (
          <AccountAccessSection
            accountId={activeAccountId}
            accountName={activeAccount?.accountName}
            isAdmin={canAdminister}
          />
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ teams

/**
 * Official teams — the ones this program deliberately created as its real rosters
 * (see {@code TeamManagement}'s discoverable checkbox) — shown clearly here, since
 * they're what another program finds when searching for an opponent. Generic teams
 * (saved as a byproduct of typing an unfamiliar opponent's name during game setup)
 * get only a plain count-and-list, on purpose: they're not vetted, so they don't
 * deserve the same visual weight. Full CRUD for either kind stays on the dedicated
 * Team Management page, linked from here.
 */
function TeamsSection({ accountId }: { accountId: string }) {
  const [teams, setTeams] = useState<SavedTeam[] | null>(null);
  const [capacity, setCapacity] = useState<TeamCapacity | null>(null);

  useEffect(() => {
    let cancelled = false;
    getTeams()
      .then(all => { if (!cancelled) setTeams(all.filter(t => t.accountId === accountId)); })
      .catch(error => console.error('Failed to load teams:', error));
    fetchTeamCapacity(accountId)
      .then(result => { if (!cancelled) setCapacity(result); })
      .catch(error => console.error('Failed to load team capacity:', error));
    return () => { cancelled = true; };
  }, [accountId]);

  const officialTeams = (teams ?? []).filter(t => t.discoverable && !t.archived);
  const genericTeams = (teams ?? []).filter(t => !t.discoverable && !t.archived);
  // 0 while capacity hasn't loaded yet, and never negative for a program already
  // over capacity (see AdditionalTeamSubscriptionService — losing a coach can leave
  // more teams than room for them).
  const emptySlots = capacity ? Math.max(0, capacity.capacity - officialTeams.length) : 0;

  return (
    <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mb-6">
      <h2 className="text-xl font-semibold text-white mb-1 flex items-center gap-2">
        <Users className="w-5 h-5 text-blue-300" />
        Teams
      </h2>
      <p className="text-sm text-blue-300/80 mb-4">
        Official teams other programs can find when they search for an opponent.
      </p>

      {teams === null ? (
        <p className="text-blue-300">Loading…</p>
      ) : officialTeams.length === 0 && emptySlots === 0 ? (
        <p className="text-sm text-blue-300/70 mb-4">
          No official teams yet — create one in Team Management and mark it discoverable.
        </p>
      ) : (
        <ul className="space-y-2 mb-4">
          {officialTeams.map(team => (
            <li
              key={team.id}
              className="flex items-start gap-2 p-3 bg-white/5 border border-white/10 rounded-xl"
            >
              <Globe className="w-4 h-4 text-blue-300 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white font-medium">{team.name}</p>
                <p className="text-xs text-blue-300/70">
                  {team.players.map(p => p.displayName).join(', ')}
                </p>
              </div>
            </li>
          ))}
          {Array.from({ length: emptySlots }, (_, index) => (
            <li
              key={`empty-slot-${index}`}
              className="flex items-center justify-center p-3 border border-dashed border-white/20 rounded-xl text-sm text-blue-300/50"
            >
              Empty Team Slot
            </li>
          ))}
        </ul>
      )}

      {genericTeams.length > 0 && (
        <p className="text-xs text-blue-300/60 mb-4">
          Plus {genericTeams.length} generic {genericTeams.length === 1 ? 'team' : 'teams'} from
          game setup, private to this program: {genericTeams.map(t => t.name).join(', ')}.
        </p>
      )}

      <Link
        to="/CurlingCodex/teams"
        className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-400 text-white px-4 py-2 rounded-lg transition-colors"
      >
        Manage teams
        <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

// ---------------------------------------------------------- account access

function AccountAccessSection({
  accountId,
  accountName,
  isAdmin,
}: {
  accountId: string;
  accountName?: string;
  /** Owner: manage anyone. Contributor: athlete rosters only — see AccountAccessService#requireMemberManage. */
  isAdmin: boolean;
}) {
  const { user, refresh } = useAuth();
  const assignableRoles = isAdmin ? ASSIGNABLE_ROLES : CONTRIBUTOR_ASSIGNABLE_ROLES;

  const [members, setMembers] = useState<AccountMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AccountRole>(isAdmin ? 'viewer' : 'athlete');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [resetLink, setResetLink] = useState<{ memberName: string; url: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setMembers(await authApi.fetchMembers(accountId));
      setError('');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load members.');
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;

    setBusy(true);
    setError('');
    setNotice('');
    try {
      const added = await authApi.addMember(accountId, email.trim(), role);
      setNotice(`${added.displayName} is now a ${ROLE_LABELS[role]}.`);
      setEmail('');
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not add that person.');
    } finally {
      setBusy(false);
    }
  }

  async function handleRoleChange(member: AccountMember, nextRole: AccountRole) {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await authApi.updateMemberRole(accountId, member.email, nextRole);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not change access.');
    } finally {
      setBusy(false);
    }
  }

  async function handleIssueResetLink(member: AccountMember) {
    setBusy(true);
    setError('');
    setNotice('');
    setCopied(false);
    try {
      const { link } = await authApi.issueMemberResetLink(accountId, member.userId);
      setResetLink({ memberName: member.displayName, url: link });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not create a reset link.');
    } finally {
      setBusy(false);
    }
  }

  async function handleCopyResetLink() {
    if (!resetLink) return;
    await navigator.clipboard.writeText(resetLink.url);
    setCopied(true);
  }

  async function handleRemove(member: AccountMember) {
    if (!confirm(`Remove ${member.displayName}'s access to this account?`)) return;

    setBusy(true);
    setError('');
    setNotice('');
    try {
      await authApi.removeMember(accountId, member.userId);
      await load();
      // Their own access list may have changed if they removed themselves elsewhere.
      await refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not remove that person.');
    } finally {
      setBusy(false);
    }
  }

  const formatDate = (timestamp: number) =>
    new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  return (
    <>
      <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mb-6">
        <h2 className="text-xl font-semibold text-white mb-1 flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-blue-300" />
          Give someone access to {accountName ?? 'this account'}
        </h2>
        <p className="text-sm text-blue-300/80 mb-4">
          They need to have registered already. Enter the email address they signed up with.
        </p>

        <form onSubmit={handleAdd} className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs uppercase tracking-wider text-blue-300/70 mb-2">
              Email address
            </label>
            <input
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); setError(''); }}
              placeholder="person@example.com"
              className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
            />
          </div>
          <div className="min-w-[180px]">
            <label className="block text-xs uppercase tracking-wider text-blue-300/70 mb-2">
              Access level
            </label>
            <select
              value={role}
              onChange={e => setRole(e.target.value as AccountRole)}
              className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
            >
              {assignableRoles.map(value => (
                <option key={value} value={value} className="bg-blue-950 text-white">
                  {ROLE_LABELS[value]}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            disabled={busy || !email.trim()}
            className="bg-blue-500 hover:bg-blue-400 disabled:opacity-50 text-white px-5 py-2 rounded-lg transition-colors"
          >
            Add
          </button>
        </form>

        <p className="text-xs text-blue-300/60 mt-3">{ROLE_DESCRIPTIONS[role]}</p>

        {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
        {notice && <p className="text-emerald-400 text-sm mt-3">{notice}</p>}
      </div>

      <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6">
        <h2 className="text-xl font-semibold text-white mb-4">People with access</h2>

        {resetLink && (
          <div className="mb-4 p-4 bg-blue-500/10 border border-blue-400/30 rounded-xl">
            <p className="text-sm text-white mb-2">
              Reset link for {resetLink.memberName} — valid for one hour. Send it to them
              directly; it isn't emailed automatically.
            </p>
            <div className="flex flex-wrap gap-2 items-center">
              <code className="flex-1 min-w-[220px] text-xs text-blue-200 bg-black/20 rounded-lg px-3 py-2 break-all">
                {resetLink.url}
              </code>
              <button
                onClick={handleCopyResetLink}
                className="flex items-center gap-1.5 bg-blue-500 hover:bg-blue-400 text-white px-3 py-2 rounded-lg text-sm transition-colors"
              >
                <Copy className="w-4 h-4" />
                {copied ? 'Copied' : 'Copy'}
              </button>
              <button
                onClick={() => { setResetLink(null); setCopied(false); }}
                className="text-blue-300/70 hover:text-white text-sm px-2 py-2"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-blue-300">Loading…</p>
        ) : (
          <div className="space-y-3">
            {members.map(member => {
              const isOwner = member.role === 'owner';
              const isSelf = member.userId === user?.id;
              // A contributor manages athlete rosters only — see requireMemberManage.
              const canManage = isAdmin || member.role === 'athlete';

              return (
                <div
                  key={member.userId}
                  className="flex flex-wrap items-center justify-between gap-3 p-4 bg-white/5 border border-white/10 rounded-xl"
                >
                  <div className="min-w-[200px]">
                    <p className="text-white font-medium flex items-center gap-2">
                      {member.displayName}
                      {isOwner && (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-300">
                          <ShieldCheck className="w-3.5 h-3.5" />
                          Primary holder
                        </span>
                      )}
                      {isSelf && !isOwner && (
                        <span className="text-xs text-blue-300/70">(you)</span>
                      )}
                    </p>
                    <p className="text-sm text-blue-300/80">{member.email}</p>
                    <p className="text-xs text-blue-300/50 mt-0.5">
                      Added {formatDate(member.grantedAt)}
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    {isOwner || !canManage ? (
                      // The owner's tier is fixed: demoting or removing them would
                      // leave the account with nobody able to administer it. A
                      // contributor sees anyone else's non-athlete access the same
                      // way — read-only, since the server would refuse a change.
                      <span className="text-sm text-blue-300/70 px-3 py-2">
                        {ROLE_LABELS[member.role]}
                      </span>
                    ) : (
                      <>
                        <select
                          value={member.role}
                          disabled={busy}
                          onChange={e =>
                            handleRoleChange(member, e.target.value as AccountRole)
                          }
                          className="bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/60 disabled:opacity-50"
                        >
                          {assignableRoles.map(value => (
                            <option key={value} value={value} className="bg-blue-950 text-white">
                              {ROLE_LABELS[value]}
                            </option>
                          ))}
                        </select>
                        {isAdmin && (
                          <button
                            onClick={() => handleIssueResetLink(member)}
                            disabled={busy}
                            title="Create a password-reset link for this person"
                            className="p-2 text-blue-300 hover:text-blue-200 hover:bg-blue-500/15 rounded-lg transition-colors disabled:opacity-50"
                          >
                            <KeyRound className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleRemove(member)}
                          disabled={busy}
                          title="Remove access"
                          className="p-2 text-red-300 hover:text-red-200 hover:bg-red-500/15 rounded-lg transition-colors disabled:opacity-50"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
